import { QueryClient, QueryClientProvider } from "react-query";
import ErrorBoundary from "./components/error-boundary";
import Stepper from "./components/stepper";
import { AuthProvider, useAuth } from "./providers/auth";
import { DashboardProvider } from './providers/dashboard'
import { HttpProvider } from "./providers/http";
import RepairScreen from './screens/repair'
import LocationScreen from "./screens/location";
import DateScreen, { withExistingOffer } from "./screens/date";
import { AppStateProvider } from "./providers/app-state";
import { FaWrench } from "react-icons/fa";
import { MdLocationPin } from "react-icons/md";
import {
    AiFillCar,
    AiOutlineCalendar,
    AiOutlineCheck,
    AiOutlinePercentage
} from "react-icons/ai";
import InfoScreen from "./screens/info";
import FinishedScreen from "./screens/finished";
import UpsaleScreen from "./screens/upsales";
import {
    BrowserRouter,
    Navigate,
    Route,
    Routes
} from "react-router-dom";
import React from "react";
import { Helmet } from 'react-helmet';
import {
    booking_meta_desc,
    booking_meta_title,
    offer_meta_desc,
    offer_meta_title
} from "./lang/da";
//import TagManager from 'react-gtm-module';
import LoginScreen from "./screens/login";
import DashboardScreen from "./screens/dashboard";
import TagManager from "react-gtm-module";
import CookieConsent from "./components/cookie-consent";

const GTMArgs = {
    gtmId: "GTM-5H976GG"
}

TagManager.initialize(GTMArgs);

const DateWithExistingOffer = withExistingOffer(DateScreen)

const queryClient = new QueryClient();

const noop = () => { }

const funnelGTMEvent = leadType => (stepName, stepOptionsFn) => (stepNumber, state, options, isBack = false) => {
    stepOptionsFn = stepOptionsFn || noop;
    TagManager.dataLayer({
        dataLayer: {
            event: "FunnelEvent",
            eventCategory: isBack ? "funnel_step_backtrack" : "funnel_step_click",
            eventAction: stepName,
            eventLabel: leadType,
            stepOptions: options || (!isBack && stepOptionsFn(state)),
            stepNumber
        }
    })
}

const GTMForwardFunnelOrder = funnelGTMEvent("booking");
const GTMForwardFunnelOffer = funnelGTMEvent("offer");

const SafeRoute = ({ children }) => {
    const { state } = useAuth();

    if (!state.token) {
        return <Navigate to="/login" replace />
    }

    return children;
}

const App = () => {
    return (
        <ErrorBoundary>
            <DashboardProvider>
                <AppStateProvider>
                    <AuthProvider>
                        <HttpProvider>
                            <QueryClientProvider
                                client={queryClient}
                            >

                                <BrowserRouter
                                    basename={process.env.REACT_APP_BASE_URL}
                                >
                                    <Routes>
                                        <Route
                                            path="/"
                                            element={<Navigate to="faatilbud" replace />}
                                        />
                                        <Route
                                            path="mitvaerksted"
                                        >
                                            <Route
                                                index={true}
                                                element={
                                                    <SafeRoute>
                                                        <DashboardScreen />
                                                    </SafeRoute>
                                                }
                                            />
                                        </Route>
                                        <Route
                                            element={<LoginScreen />}
                                            path="login"
                                        />
                                        <Route
                                            path="booking"
                                            element={
                                                <>
                                                    <Helmet>
                                                        <title>
                                                            {booking_meta_title}
                                                        </title>
                                                        <meta
                                                            name="description"
                                                            content={booking_meta_desc}
                                                        />
                                                    </Helmet>
                                                    <Stepper
                                                        steps={[
                                                            {
                                                                stepName: "afdeling",
                                                                stepElement: <LocationScreen />,
                                                                stepRule: state => Boolean(state.selectedShop),
                                                                icon: <MdLocationPin />,
                                                                fullScreen: false,
                                                                trackerFn: GTMForwardFunnelOrder(
                                                                    "vælg_afdeling"
                                                                )
                                                            },
                                                            {
                                                                stepName: "reparationer",
                                                                stepElement: <RepairScreen />,
                                                                stepRule: state => Boolean(state.selectedRepairs.length) || !!state.repairComment,
                                                                icon: <FaWrench />,
                                                                fullScreen: false,
                                                                trackerFn: GTMForwardFunnelOrder(
                                                                    "vælg_reparationer",
                                                                    state => (
                                                                        state.selectedRepairs.reduce(
                                                                            (acc, cur) => (
                                                                                acc + cur.name + ", "
                                                                            ),
                                                                            ""
                                                                        )
                                                                    )
                                                                )
                                                            },
                                                            {
                                                                stepName: "rabatter",
                                                                stepElement: <UpsaleScreen />,
                                                                stepRule: state => Boolean(state.selectedRepairs.length),
                                                                icon: <AiOutlinePercentage />,
                                                                fullScreen: false,
                                                                hiddenFn: state => state.selectedRepairs.some(rep => Boolean(rep.categoryUpsaleProducts || rep.secondLevelCategoryUpsaleProducts)),
                                                                trackerFn: GTMForwardFunnelOrder(
                                                                    "vælg_opsalg",
                                                                    state => (
                                                                        state.selectedUpsales.reduce(
                                                                            (acc, cur) => (
                                                                                acc + cur.name + ", "
                                                                            ),
                                                                            ""
                                                                        )
                                                                    )
                                                                )
                                                            },
                                                            {
                                                                stepName: "tid&dato",
                                                                stepElement: <DateScreen />,
                                                                stepRule: state => (state.pickupTime && state.pickupDate && state.selectedDate && state.deliveryTime) || state.timeOption === 2,
                                                                icon: <AiOutlineCalendar />,
                                                                fullScreen: false,
                                                                trackerFn: GTMForwardFunnelOrder(
                                                                    "vælg_dato"
                                                                )
                                                            },
                                                            {
                                                                stepName: "mine info",
                                                                stepElement: <InfoScreen />,
                                                                stepRule: state => state.email.valid && state.name.valid && state.phone.valid && (!state.plate.value || state.plate.valid),
                                                                icon: <AiFillCar />,
                                                                fullScreen: true,
                                                                trackerFn: GTMForwardFunnelOrder(
                                                                    "vælg_info"
                                                                )
                                                            },
                                                            {
                                                                stepName: "finished",
                                                                stepElement: <FinishedScreen />,
                                                                icon: <AiOutlineCheck />,
                                                                stepRule: _ => false,
                                                                fullScreen: true
                                                            },
                                                        ]}
                                                    />
                                                </>
                                            }
                                        />
                                        <Route
                                            path="existingoffer/:shopId/:plate/:orderId"
                                            element={
                                                <Stepper
                                                    validateOffer={true}
                                                    steps={[
                                                        {
                                                            stepName: "tid&dato",
                                                            stepElement: <DateWithExistingOffer />,
                                                            stepRule: state => (state.pickupTime && state.pickupDate && state.selectedDate && state.deliveryTime) || state.timeOption === 2,
                                                            icon: <AiOutlineCalendar />,
                                                            fullScreen: true
                                                        },
                                                        {
                                                            stepName: "mine info",
                                                            stepElement: <InfoScreen />,
                                                            stepRule: state => state.email.valid && state.name.valid && state.phone.valid && (!state.plate.value || state.plate.valid),
                                                            icon: <AiFillCar />,
                                                            fullScreen: true,
                                                        },
                                                        {
                                                            stepName: "finished",
                                                            stepElement: <FinishedScreen />,
                                                            icon: <AiOutlineCheck />,
                                                            stepRule: _ => false,
                                                            fullScreen: true
                                                        },
                                                    ]}
                                                />
                                            }
                                        />
                                        <Route
                                            path="faatilbud"
                                            element={
                                                <>
                                                    <Helmet>
                                                        <title>
                                                            {offer_meta_title}
                                                        </title>
                                                        <meta
                                                            name="description"
                                                            content={offer_meta_desc}
                                                        />
                                                    </Helmet>
                                                    <Stepper
                                                        steps={[
                                                            {
                                                                stepName: "afdeling",
                                                                stepElement: <LocationScreen />,
                                                                stepRule: state => Boolean(state.selectedShop),
                                                                icon: <MdLocationPin />,
                                                                fullScreen: false,
                                                                trackerFn: GTMForwardFunnelOffer(
                                                                    "vælg_afdeling",
                                                                )
                                                            },
                                                            {
                                                                stepName: "reparationer",
                                                                stepElement: <RepairScreen />,
                                                                stepRule: state => Boolean(state.selectedRepairs.length) || !!state.repairComment,
                                                                icon: <FaWrench />,
                                                                fullScreen: false,
                                                                trackerFn: GTMForwardFunnelOffer(
                                                                    "vælg_reparationer",
                                                                    state => (
                                                                        state.selectedRepairs.reduce(
                                                                            (acc, cur) => (
                                                                                acc + cur.name + ", "
                                                                            ),
                                                                            ""
                                                                        )
                                                                    )
                                                                )
                                                            },
                                                            {
                                                                stepName: "rabatter",
                                                                stepElement: <UpsaleScreen />,
                                                                stepRule: state => Boolean(state.selectedRepairs.length),
                                                                icon: <AiOutlinePercentage />,
                                                                fullScreen: false,
                                                                hiddenFn: state => state.selectedRepairs.some(rep => Boolean(rep.categoryUpsaleProducts || rep.secondLevelCategoryUpsaleProducts)),
                                                                trackerFn: GTMForwardFunnelOffer(
                                                                    "vælg_opsalg",
                                                                    state => (
                                                                        state.selectedUpsales.reduce(
                                                                            (acc, cur) => (
                                                                                acc + cur.name + ", "
                                                                            ),
                                                                            ""
                                                                        )
                                                                    )
                                                                )
                                                            },
                                                            {
                                                                stepName: "mine info",
                                                                stepElement: <InfoScreen />,
                                                                stepRule: state => state.email.valid && state.name.valid && state.phone.valid && (!state.plate.value || state.plate.valid),
                                                                icon: <AiFillCar />,
                                                                fullScreen: true,
                                                                trackerFn: GTMForwardFunnelOffer(
                                                                    "vælg_info"
                                                                )
                                                            },
                                                            {
                                                                stepName: "finished",
                                                                stepElement: <FinishedScreen />,
                                                                icon: <AiOutlineCheck />,
                                                                stepRule: _ => false,
                                                                fullScreen: true
                                                            },
                                                        ]}
                                                    />
                                                </>
                                            }
                                        />
                                    </Routes>
                                </BrowserRouter>
                            </QueryClientProvider>
                        </HttpProvider>
                    </AuthProvider>
                </AppStateProvider>
            </DashboardProvider>
        </ErrorBoundary>
    )
}


export default App;